/**
 * The trilingual reason-code catalog for matcher-v2 fit facts (PIVOT-301
 * Acceptance: "The client renders every reason code in French, Dutch, and
 * English."). Consumed by `components/MatcherV2Card.tsx`, which never
 * renders a raw `MatchFitFacts.concerns` string directly.
 *
 * Mirrors the FIXED reason-code vocabulary in
 * `backend/src/pivot/domain/fit_assessment.py` (the `REASON_*` constants)
 * and `backend/src/pivot/domain/selector_input.py`'s
 * `GateDecision.reason_code` — every string either module can ever emit
 * into a `FitAssessment.concerns` entry or a `rejection_reason`. Two facts
 * about that vocabulary drive this module's shape:
 *
 * 1. It is a CLOSED set today (11 `REASON_*` constants + 4 gate codes) —
 *    `REASON_TO_KEY` below is a `Record` keyed on that exact union, so
 *    dropping a case or misspelling one is a compile error, never a silent
 *    fallback.
 * 2. One code is not a bare string but a PREFIX: `unknown_rule_fact:<rule_id>`
 *    (`fit_assessment.py`'s own comment: "this suffixed form IS the literal
 *    string pushed into concerns"). `rule_id` is free-form policy content
 *    with no fixed catalog of its own (no production caller builds a
 *    `RuleToJudge` yet), so this module deliberately never tries to
 *    translate the `rule_id` half — it renders the FIXED
 *    `reasonUnknownRuleFact` copy regardless of which rule triggered it.
 *
 * `reasonCodeKey` is the one function a card calls: an exact match against
 * the closed set, then the one prefix pattern above, then
 * `matcherV2.reasonUnrecognized` — a tested, explicit fallback (never the
 * raw code rendered verbatim) for a code a future backend change adds
 * before this catalog is updated to match.
 */

import type { TranslationKey } from '../i18n/types'

const UNKNOWN_RULE_FACT_PREFIX = 'unknown_rule_fact:'

/** The closed set of reason codes this module recognizes by exact string
 * match. See the module docstring for where each one comes from. */
const REASON_TO_KEY: Record<string, TranslationKey> = {
  demand_met: 'matcherV2.reasonDemandMet',
  demand_met_unresolved: 'matcherV2.reasonDemandMetUnresolved',
  demand_gap: 'matcherV2.reasonDemandGap',
  core_demand_gap_verified: 'matcherV2.reasonCoreDemandGapVerified',
  core_demand_gap_unresolved: 'matcherV2.reasonCoreDemandGapUnresolved',
  hard_failure_verified: 'matcherV2.reasonHardFailureVerified',
  hard_failure_unresolved: 'matcherV2.reasonHardFailureUnresolved',
  rule_confirmed: 'matcherV2.reasonRuleConfirmed',
  soft_rule_contradicted: 'matcherV2.reasonSoftRuleContradicted',
  unverified_fact: 'matcherV2.reasonUnverifiedFact',
  // The bare form (no rule id suffix) is not something the backend emits on
  // its own, but is kept as an exact-match entry too: a caller that already
  // stripped the suffix (or a future backend change that emits the bare
  // constant) still resolves to the same copy as the prefixed form.
  unknown_rule_fact: 'matcherV2.reasonUnknownRuleFact',
  hard_location_conflict: 'matcherV2.reasonHardLocationConflict',
  hard_contract_conflict: 'matcherV2.reasonHardContractConflict',
  hard_work_exclusion_conflict: 'matcherV2.reasonHardWorkExclusionConflict',
  required_language_known_low: 'matcherV2.reasonRequiredLanguageKnownLow',
}

/**
 * The `TranslationKey` for one reason code — exact match, then the
 * `unknown_rule_fact:` prefix, then the tested fallback. Never returns the
 * raw code: a card renders `t(reasonCodeKey(code))`, always a real,
 * localized sentence.
 */
export function reasonCodeKey(code: string): TranslationKey {
  const exact = REASON_TO_KEY[code]
  if (exact) return exact
  if (code.startsWith(UNKNOWN_RULE_FACT_PREFIX)) return 'matcherV2.reasonUnknownRuleFact'
  return 'matcherV2.reasonUnrecognized'
}

/**
 * Which section of the card a reason code belongs in (plan §1.7/§5.2: "the
 * fit tier, the visible concerns, and the unverified-job-fact notes" name
 * these as two different things). `unverified_fact` is the one code that
 * means "the posting didn't say", not "something is wrong" — every other
 * code is a concern.
 */
export type ReasonCategory = 'concern' | 'note'

export function reasonCodeCategory(code: string): ReasonCategory {
  return code === 'unverified_fact' ? 'note' : 'concern'
}
